// src/context/JukeboxContext.js
//
// Manages the Jukebox feature: which version of each jukebox-enabled sound is
// active, per tab. State is persisted in AsyncStorage so selections survive
// app restarts. The setVersion() call also handles crossfading the live audio
// layer when the sound is currently playing.
//
// Structure of persisted data:
//   { sleep: { rain: 'rain-v2', water: 'water-v1' }, study: {}, kids: {} }
//
// Tabs: 'sleep' | 'study' | 'kids'

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { AudioEngine } from '../audio/AudioEngine';

const STORAGE_KEY = 'sleepmix.jukebox.v1';

const JukeboxContext = createContext(null);

export function JukeboxProvider({ children }) {
  // { [tabId]: { [soundId]: versionId } }
  const [selections, setSelections] = useState({});

  // Load persisted selections on mount
  useEffect(() => {
    AsyncStorage.getItem(STORAGE_KEY)
      .then((raw) => { if (raw) setSelections(JSON.parse(raw)); })
      .catch(() => {});
  }, []);

  // Returns the active URI for a sound on a given tab.
  // Falls back to sound.uri (= versions[0]) when nothing is selected yet.
  const getActiveUri = useCallback(
    (tabId, sound) => {
      if (!sound.jukeboxEnabled || !sound.versions?.length) return sound.uri;
      const versionId = selections[tabId]?.[sound.id];
      if (!versionId) return sound.uri; // default = first version
      const version = sound.versions.find((v) => v.id === versionId);
      return version?.uri ?? sound.uri;
    },
    [selections],
  );

  // Returns the currently selected version ID for a sound, or null (= default).
  const getActiveVersionId = useCallback(
    (tabId, soundId) => selections[tabId]?.[soundId] ?? null,
    [selections],
  );

  // Selects a version, persists the choice, and crossfades the live audio
  // layer if the sound is currently playing.
  const setVersion = useCallback(async (tabId, sound, version) => {
    const soundId = sound.id;
    const newUri = version.uri;

    // Update state and persist
    setSelections((prev) => {
      const next = {
        ...prev,
        [tabId]: {
          ...(prev[tabId] ?? {}),
          [soundId]: version.id,
        },
      };
      AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(next)).catch(() => {});
      return next;
    });

    // Crossfade if the layer is currently active in the audio engine
    const activeLayer = AudioEngine.getState().find((l) => l.id === soundId);
    if (activeLayer) {
      const vol = activeLayer.volume;
      // Fade out old version quickly, then fade in new version
      AudioEngine.stopLayer(soundId, { fadeMs: 600 }).catch(() => {});
      setTimeout(() => {
        AudioEngine.playLayer(soundId, {
          uri: newUri,
          name: sound.name,
          volume: vol,
          artworkUri: sound.artworkUri ?? null,
          fadeMs: 1200,
        }).catch(() => {});
      }, 700);
    }
  }, []);

  return (
    <JukeboxContext.Provider
      value={{ getActiveUri, getActiveVersionId, setVersion }}
    >
      {children}
    </JukeboxContext.Provider>
  );
}

export const useJukebox = () => useContext(JukeboxContext);
